/**
 * useUpdateParcelaManual — edição/cancelamento de PARCELA individual em
 * cobranças criadas em conta corrente SEM integração (controle manual).
 *
 * Em cobranças com provider (ex.: Asaas) o próprio provedor gerencia as
 * parcelas; por isso a RPC `update_financeiro_parcela_manual` recusa qualquer
 * lançamento que tenha provider e registra a tentativa em
 * `security_bypass_attempts`. Este hook é apenas o caminho de UI.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-12 v1.0.0 - Versão inicial (vencimento, valor, descrição e
 *                              cancelamento por parcela em conta manual).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useUpdateParcelaManual');

export interface UpdateParcelaManualInput {
  parcela_id: string;
  /** 'update' altera campos; 'cancel' cancela apenas aquela parcela */
  acao: 'update' | 'cancel';
  data_vencimento?: string | null;
  valor?: number | null;
  descricao?: string | null;
  cancel_reason?: string | null;
}

export function useUpdateParcelaManual() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: UpdateParcelaManualInput) => {
      const { data, error } = await supabase.rpc(
        'update_financeiro_parcela_manual' as never,
        {
          p_parcela_id: input.parcela_id,
          p_acao: input.acao,
          p_data_vencimento: input.data_vencimento ?? null,
          p_valor: input.valor ?? null,
          p_descricao: input.descricao ?? null,
          p_cancel_reason: input.cancel_reason ?? null,
        } as never,
      );

      if (error) {
        logger.error('Falha ao alterar parcela manual', { error: error.message });
        throw new Error(error.message);
      }

      return data as unknown as Record<string, unknown>;
    },
    onSuccess: (_data, variables) => {
      // Mantém listas, KPIs e o modal de detalhes coerentes com o novo total
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
      toast.success(
        variables.acao === 'cancel' ? 'Parcela cancelada' : 'Parcela atualizada',
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Não foi possível alterar a parcela');
    },
  });

  return {
    updateParcela: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
}
